/**
 * Intake Year × Intake Month pairing (study abroad).
 *
 * Intake is stored as TWO independent SELECTOR custom fields — "Intake Year"
 * (2024…2029) and "Intake Month" (January…December). Filtering them
 * independently is a CROSS PRODUCT: years [2026, 2027] + months [Dec, Jan]
 * matches Jan 2026 and Dec 2027 too, so the obvious question — "December 2026
 * or January 2027" — could not be asked at all.
 *
 * Once at least one year is selected, the Intake Month picker therefore lists
 * YEAR-QUALIFIED options ("December 2026", "January 2027") and the selection is
 * sent as correlated pairs (`cfPair_<yearField>_<monthField>=<yearOpt>-<monthOpt>`),
 * which the server matches on ONE deal. See `lead.cf-pairs.ts` server-side.
 *
 * Field DETECTION mirrors the server's `resolveIntakeCfMaps`
 * (study-abroad-dashboard-scope.ts) exactly — tagged `spesificTypes: "Intake"`
 * or a name containing "intake", then classified by whether its options parse
 * as months or years — so the two ends can never disagree about which custom
 * fields make up an intake.
 */

const MONTH_NUM: Record<string, number> = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  jan: 1, feb: 2, mar: 3, apr: 4, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9,
  oct: 10, nov: 11, dec: 12,
};

const SHORT_MONTH = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** `"March"` / `"mar"` / `"3"` → 3; anything else → null. */
export function parseIntakeMonth(value: string): number | null {
  const named = MONTH_NUM[String(value).trim().toLowerCase()];
  if (named) return named;
  const num = Number(String(value).trim());
  return Number.isInteger(num) && num >= 1 && num <= 12 ? num : null;
}

/** `"2026"` → 2026; anything outside 2000–2100 → null. */
export function parseIntakeYear(value: string): number | null {
  const num = Number(String(value).trim());
  return Number.isInteger(num) && num >= 2000 && num <= 2100 ? num : null;
}

export interface IntakeCfOption {
  id: number | string;
  value: string;
}

export interface IntakeCfField {
  id: number | string;
  name: string;
  options: IntakeCfOption[];
  spesificTypes?: string;
}

export interface IntakePairFields {
  yearField: IntakeCfField;
  monthField: IntakeCfField;
}

const hasOptions = (f: any) => Array.isArray(f?.options) && f.options.length > 0;

export const looksLikeIntakeField = (f: any) =>
  f?.spesificTypes === "Intake" ||
  String(f?.name ?? "").toLowerCase().includes("intake");

/**
 * Finds the tenant's Intake Year + Intake Month fields among a list of SELECTOR
 * custom fields. Returns `null` unless BOTH exist — a tenant with only one of
 * them has nothing to correlate and keeps the plain single-field picker.
 */
export function detectIntakePairFields(
  fields: any[] | undefined,
): IntakePairFields | null {
  let yearField: IntakeCfField | null = null;
  let monthField: IntakeCfField | null = null;

  for (const field of fields ?? []) {
    if (!hasOptions(field) || !looksLikeIntakeField(field)) continue;

    // Classify by what the options actually ARE, not by the field's name: a
    // field named "Intake" holding 2024…2029 is the year field.
    let monthHits = 0;
    let yearHits = 0;
    for (const opt of field.options) {
      if (parseIntakeMonth(opt.value) != null) monthHits++;
      else if (parseIntakeYear(opt.value) != null) yearHits++;
    }
    if (monthHits >= yearHits && monthHits > 0) {
      monthField ??= field;
    } else if (yearHits > 0) {
      yearField ??= field;
    }
  }

  return yearField && monthField ? { yearField, monthField } : null;
}

/** Bucket key for a pair filter: `"<yearFieldId>_<monthFieldId>"`. */
export const intakePairBucketKey = (
  yearFieldId: number | string,
  monthFieldId: number | string,
) => `${yearFieldId}_${monthFieldId}`;

/** Wire token for one selected combination: `"<yearOptionId>-<monthOptionId>"`. */
export const encodeIntakePair = (
  yearOptionId: number | string,
  monthOptionId: number | string,
) => `${yearOptionId}-${monthOptionId}`;

export function decodeIntakePair(
  token: string,
): { yearOptionId: string; monthOptionId: string } | null {
  const [yearOptionId, monthOptionId, ...rest] = String(token).trim().split("-");
  if (rest.length > 0 || !yearOptionId || !monthOptionId) return null;
  return { yearOptionId, monthOptionId };
}

export const splitPairTokens = (raw: string | undefined): string[] =>
  raw ? raw.split(",").map((t) => t.trim()).filter(Boolean) : [];

export interface IntakePairOption {
  /** `"<yearOptionId>-<monthOptionId>"` */
  value: string;
  /** e.g. `"January 2026"` */
  label: string;
  yearOptionId: string;
  yearLabel: string;
  monthOptionId: string;
  monthLabel: string;
  /** Sort/grouping helpers. */
  year: number;
  month: number;
}

/**
 * Every (selected year × every month) combination, ordered year-major then
 * calendar-month — so the list reads "January 2026 … December 2026, January
 * 2027 …" and can be grouped under a year heading.
 *
 * Options whose value doesn't parse (a stray "TBD" entry) are skipped rather
 * than sorted arbitrarily into the calendar.
 */
export function buildIntakePairOptions(
  pair: IntakePairFields,
  selectedYearOptionIds: string[],
): IntakePairOption[] {
  const selected = new Set(selectedYearOptionIds.map(String));

  const years = pair.yearField.options
    .filter((o) => selected.has(String(o.id)))
    .map((o) => ({ opt: o, year: parseIntakeYear(o.value) }))
    .filter((y): y is { opt: IntakeCfOption; year: number } => y.year != null)
    .sort((a, b) => a.year - b.year);

  const months = pair.monthField.options
    .map((o) => ({ opt: o, month: parseIntakeMonth(o.value) }))
    .filter((m): m is { opt: IntakeCfOption; month: number } => m.month != null)
    .sort((a, b) => a.month - b.month);

  const out: IntakePairOption[] = [];
  for (const y of years) {
    for (const m of months) {
      out.push({
        value: encodeIntakePair(y.opt.id, m.opt.id),
        label: `${m.opt.value} ${y.opt.value}`,
        yearOptionId: String(y.opt.id),
        yearLabel: y.opt.value,
        monthOptionId: String(m.opt.id),
        monthLabel: m.opt.value,
        year: y.year,
        month: m.month,
      });
    }
  }
  return out;
}

/**
 * Human label for a stored pair token, e.g. `"Dec 2026"` — used by the applied
 * chips, which resolve option ids without the picker's option list in hand.
 * Falls back to the raw token when either id is unknown (a deleted option).
 */
export function formatIntakePairLabel(
  token: string,
  yearOptions: Record<string, string>,
  monthOptions: Record<string, string>,
): string {
  const decoded = decodeIntakePair(token);
  if (!decoded) return token;
  const yearLabel = yearOptions[decoded.yearOptionId];
  const monthRaw = monthOptions[decoded.monthOptionId];
  if (!yearLabel || !monthRaw) return token;
  const monthNum = parseIntakeMonth(monthRaw);
  const monthLabel = monthNum ? SHORT_MONTH[monthNum - 1] : monthRaw;
  return `${monthLabel} ${yearLabel}`;
}

/** Sort tokens chronologically for a stable chip/trigger reading. */
export function sortIntakePairTokens(
  tokens: string[],
  yearOptions: Record<string, string>,
  monthOptions: Record<string, string>,
): string[] {
  const rank = (token: string) => {
    const decoded = decodeIntakePair(token);
    if (!decoded) return Number.MAX_SAFE_INTEGER;
    const year = parseIntakeYear(yearOptions[decoded.yearOptionId] ?? "") ?? 9999;
    const month = parseIntakeMonth(monthOptions[decoded.monthOptionId] ?? "") ?? 13;
    return year * 100 + month;
  };
  return [...tokens].sort((a, b) => rank(a) - rank(b));
}
